import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import publicApi from '../api/publicApi';
import styles from './StripeCheckout.module.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_REEMPLAZA_CON_TU_CLAVE_PUBLICA');

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: '16px',
            color: '#1e293b',
            fontFamily: "'Inter', 'Segoe UI', sans-serif",
            '::placeholder': {
                color: '#94a3b8',
            },
            iconColor: '#10b981',
        },
        invalid: {
            color: '#ef4444',
            iconColor: '#ef4444',
        },
    },
    hidePostalCode: true,
};

function CheckoutForm({ amount, ticketId, onSuccess, onError }) {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [cardError, setCardError] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [loadingIntent, setLoadingIntent] = useState(true);

    useEffect(() => {
        const fetchIntent = async () => {
            try {
                const { data } = await publicApi.post('/create-stripe-intent', {
                    amount,
                    ticketId,
                });
                setClientSecret(data.clientSecret);
            } catch (err) {
                onError('No se pudo iniciar el pago con tarjeta. Intenta de nuevo.');
            } finally {
                setLoadingIntent(false);
            }
        };

        fetchIntent();
    }, [amount, ticketId, onError]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || !clientSecret) return;

        setProcessing(true);
        setCardError('');

        const card = elements.getElement(CardElement);

        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: { card },
        });

        if (error) {
            setCardError(error.message);
            setProcessing(false);
        } else if (paymentIntent.status === 'succeeded') {
            onSuccess(paymentIntent.id);
        }
    };

    if (loadingIntent) {
        return (
            <div className={styles.loadingIntent}>
                <div className={styles.spinner}></div>
                <span>Preparando pago seguro...</span>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className={styles.form}>
            <p className={styles.secureNote}>
                🔒 Pago seguro procesado por Stripe
            </p>

            <div className={styles.cardWrapper}>
                <label className={styles.cardLabel}>Datos de la Tarjeta</label>
                <div className={styles.cardElementBox}>
                    <CardElement options={CARD_ELEMENT_OPTIONS} />
                </div>
                <p className={styles.cardHint}>
                    Visa, Mastercard, American Express y más
                </p>
            </div>

            {cardError && (
                <p className={styles.cardError}>{cardError}</p>
            )}

            <button
                type="submit"
                disabled={!stripe || processing || !clientSecret}
                className={styles.payButton}
            >
                {processing ? (
                    <>
                        <span className={styles.spinner}></span>
                        Procesando...
                    </>
                ) : (
                    `Pagar $${Number(amount).toFixed(2)} USD`
                )}
            </button>
        </form>
    );
}

function StripeCheckout({ amount, ticketId, onSuccess, onError }) {
    return (
        <Elements stripe={stripePromise}>
            <CheckoutForm
                amount={amount}
                ticketId={ticketId}
                onSuccess={onSuccess}
                onError={onError}
            />
        </Elements>
    );
}

export default StripeCheckout;
